import {atom} from 'jotai';
import {getGroupDataById, getGroups} from '@/actions/groups';
import type {Group, GroupData, GroupsState} from '@/types/groups';
import type {CategoriesState, Category} from '@/types/categories';
import {getCategories} from '@/actions/groups/categories';

const initialState: GroupsState = {
    total: 0,
    items: [],
    error: null,
    loading: false,
    initialized: false,
};

export const groupsAtom = atom<GroupsState>(initialState);
export const selectedGroupAtom = atom<Group | null>(null);
export const selectedGroupDataAtom = atom<GroupData | null>(null);

export const groupsSearchParamsAtom = atom({
    page: 1,
    name: '',
    sort_order: 'desc' as const,
    tagId: null as number | null,
    category: null as string | null,
    subcategory: null as string | null,
    state: '' as '' | 'hidden' | 'shared',
    user_id: undefined as number | undefined,
});

export const groupCategoriesAtom = atom<CategoriesState>({
    items: [],
    error: null,
    loading: false,
});

// Array to store a selected category path (supports unlimited nesting levels)
export const selectedGroupCategoryPathAtom = atom<Category[]>([]);

export const getGroupsAtom = atom(null, () => getGroups());
export const getGroupDataByIdAtom = atom(null, async (get, set, id: number) => {
    await getGroupDataById(get, set, id);
});

export const getGroupCategoriesAtom = atom(null, async (get, set) => {
    set(groupCategoriesAtom, (prev: CategoriesState) => ({
        ...prev,
        error: null,
        loading: true,
    }));

    try {
        const rawCategories = await getCategories();
        // Dedupe by id using Map (equivalent to lodash uniqBy)
        const categories = Array.from(
            new Map(rawCategories.map((c) => [c.id, c])).values()
        );

        const rootCategories: Category[] = [];
        const categoryMap = new Map<string, Category>();

        categories.forEach((category) => {
            categoryMap.set(category.id, {...category, children: []});
        });

        categoryMap.forEach((category) => {
            if (!category.parent_id) return rootCategories.push(category);
            const parent = categoryMap.get(category.parent_id);
            if (parent?.children) {
                parent.children.push(category);
            }
        });

        set(groupCategoriesAtom, {
            error: null,
            loading: false,
            items: rootCategories,
        });
    } catch (error) {
        set(groupCategoriesAtom, {
            items: [],
            loading: false,
            error:
                error instanceof Error
                    ? error.message
                    : 'Failed to fetch categories',
        });
    }
});
